import React, { useEffect, useMemo, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import { EntityTypeEnum, LovDTO } from '@api/ringil-api.v2.tsx';
import InputFolders from '@components/framework/input/InputFolders/InputFolders.tsx';
import { useFoldersState } from '@hooks/use-folders/useFolders.tsx';

interface ComponentProps {
  secured?: SecuredConfig;
  required?: boolean;
  readonly?: boolean;
  folders: LovDTO[];
  /** Receives only the folders the user may edit; the backend keeps the locked ones (RD-2977 option 1). */
  onChange: (folders: LovDTO[]) => void;
  entityType: EntityTypeEnum;
  errorMessage?: string;
}

/**
 * RD-2977: an entity can be filed in several folders; the tag shows them comma separated.
 * Folders the user may not see (filed by somebody else, e.g. a department folder) are shown locked:
 * they are listed but cannot be removed, and the picker only edits the user's accessible part.
 */
const InternalInfoFoldersTag: React.FC<ComponentProps> = ({ secured, readonly, required, folders, onChange, entityType, errorMessage }) => {
  const { tr } = useTranslationLgs();
  // until the LOV answers we cannot tell locked from editable - saving then could drop the user's own folders
  const { folders: accessible, loaded } = useFoldersState(entityType);
  const accessibleIds = useMemo(() => new Set(accessible.map(f => f.id)), [accessible]);

  const isLocked = (f: LovDTO) => loaded && !accessibleIds.has(f.id);
  const locked = useMemo(() => (folders ?? []).filter(f => loaded && !accessibleIds.has(f.id)), [folders, accessibleIds, loaded]);
  const editable = useMemo(() => (folders ?? []).filter(f => !loaded || accessibleIds.has(f.id)), [folders, accessibleIds, loaded]);

  const [internalValue, setInternalValue] = useState<LovDTO[]>(editable);
  useEffect(() => {
    setInternalValue(editable);
  }, [editable]);

  const renderText = (value: LovDTO[]) =>
    value && value.length > 0 ? (
      <span>
        {value.map((f, idx) => (
          <span key={f.id} title={isLocked(f) ? tr('InternalInfoFoldersTag.lockedHint', 'Složka, ke které nemáte přístup - nelze ji odebrat') : undefined}>
            {idx > 0 && ', '}
            {f.name}
            {isLocked(f) && ' 🔒'}
          </span>
        ))}
      </span>
    ) : null;

  return (
    <ButtonTag
      secured={secured}
      label={tr('InternalInfoFoldersTag.folders', 'Složky')}
      required={required}
      readonly={readonly}
      value={renderText(folders)}
      errorMessage={errorMessage}
      saveDisabled={!loaded}
      onSave={onSuccess => {
        onChange(internalValue);
        onSuccess();
      }}
      autoClose
      dataTestId="internalInfo-folders"
    >
      <div className={'min-w-[320px] flex flex-col gap-2'}>
        {locked.length > 0 && (
          <div className={'text-sm text-content-secondary'}>
            {tr('InternalInfoFoldersTag.locked', 'Bez vašeho přístupu (zůstanou zachovány)')}: {locked.map(f => f.name).join(', ')}
          </div>
        )}
        <InputFolders folders={internalValue} options={accessible} onChange={setInternalValue} entityType={entityType} dataTestIdBase="internalInfo-folderList" />
      </div>
    </ButtonTag>
  );
};

export default InternalInfoFoldersTag;
